import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useActiveRoute} from '@github-ui/pull-requests/hooks/use-active-route'
import type {LayoutStoreState} from '@github-ui/pull-requests/layout-store'
import {useAnalytics} from '@github-ui/use-analytics'
import {useCallback, useMemo} from 'react'

type UnifiedBatchItem = Parameters<LayoutStoreState['unifiedBatchActions']['addItem']>[0]
type UnifiedBatchItemKind = UnifiedBatchItem['kind']

export type UnifiedCommentActionTelemetryItem = {
  hasSuggestion: boolean
  isOutdated: boolean
  kind: UnifiedBatchItemKind
  suggestionIndex?: number
}

/**
 * Telemetry for unified PR review batch per-comment actions.
 *
 * Covers both placements used by the unified Files Changed flow:
 * - `unified_resolve_row` for whole-comment actions on comments without suggestions
 * - `per_suggestion_controls` for suggestion-level Add/Remove/Commit controls
 */
export const UnifiedCommentActionTelemetryEvents = {
  resolveThreadClicked: 'resolve_thread_clicked',
  fixWithCopilotClicked: 'fix_with_copilot_clicked',
  addToBatchClicked: 'add_to_batch_clicked',
  removeFromBatchClicked: 'remove_from_batch_clicked',
  commitSuggestionsClicked: 'commit_suggestions_clicked',
} as const

export type UnifiedCommentActionTelemetryEvent =
  (typeof UnifiedCommentActionTelemetryEvents)[keyof typeof UnifiedCommentActionTelemetryEvents]

export type SendUnifiedCommentActionTelemetry = (eventName: UnifiedCommentActionTelemetryEvent) => void

export const UnifiedCommentActionTelemetryPlacements = {
  unifiedResolveRow: 'unified_resolve_row',
  perSuggestionControls: 'per_suggestion_controls',
} as const

export type UnifiedCommentActionTelemetryPlacement =
  (typeof UnifiedCommentActionTelemetryPlacements)[keyof typeof UnifiedCommentActionTelemetryPlacements]

type UnifiedCommentActionTelemetryMetadata = {
  commentKind: 'ccr' | 'human'
  commentKindDetail: UnifiedCommentActionTelemetryItem['kind']
  controlPlacement: UnifiedCommentActionTelemetryPlacement
  hasSuggestion: boolean
  isOutdated: boolean
  surface: string
  suggestionIndex?: number
}

const UNIFIED_COMMENT_ACTION_TARGET = 'UNIFIED_COMMENT_ACTIONS'

type UnifiedCommentActionTelemetryMetadataOptions = {
  controlPlacement: UnifiedCommentActionTelemetryPlacement
  surface: string
}

type UnifiedCommentActionTelemetryOptions = {
  controlPlacement: UnifiedCommentActionTelemetryPlacement
}

function buildUnifiedCommentActionTelemetryMetadata(
  item: UnifiedCommentActionTelemetryItem,
  {controlPlacement, surface}: UnifiedCommentActionTelemetryMetadataOptions,
): UnifiedCommentActionTelemetryMetadata {
  const metadata: Omit<UnifiedCommentActionTelemetryMetadata, 'suggestionIndex'> = {
    commentKind: item.kind.startsWith('ccr') ? 'ccr' : 'human',
    commentKindDetail: item.kind,
    controlPlacement,
    hasSuggestion: item.hasSuggestion,
    isOutdated: item.isOutdated,
    surface,
  }

  return item.suggestionIndex === undefined ? metadata : {...metadata, suggestionIndex: item.suggestionIndex}
}

export function useUnifiedCommentActionTelemetry(
  item: UnifiedCommentActionTelemetryItem | undefined,
  {controlPlacement}: UnifiedCommentActionTelemetryOptions,
): SendUnifiedCommentActionTelemetry {
  const surface = useActiveRoute()
  const {sendAnalyticsEvent} = useAnalytics()
  const hasSuggestion = item?.hasSuggestion
  const isOutdated = item?.isOutdated
  const kind = item?.kind
  const suggestionIndex = item?.suggestionIndex

  const metadata = useMemo(() => {
    if (kind === undefined || hasSuggestion === undefined || isOutdated === undefined) return undefined

    return buildUnifiedCommentActionTelemetryMetadata(
      {hasSuggestion, isOutdated, kind, suggestionIndex},
      {controlPlacement, surface},
    )
  }, [controlPlacement, hasSuggestion, isOutdated, kind, suggestionIndex, surface])

  return useCallback(
    (eventName: UnifiedCommentActionTelemetryEvent) => {
      if (!metadata || !isFeatureEnabled('unified_batch_pr_comments')) return

      sendAnalyticsEvent(eventName, UNIFIED_COMMENT_ACTION_TARGET, metadata)
    },
    [metadata, sendAnalyticsEvent],
  )
}
